import React, { useEffect, useState } from 'react';
import config from '../config';
import { formatFabricTagLabel } from '../utils/fabricTagCategories';
import { adminFetch } from '../utils/adminAuth';

const API_BASE = config.API_BASE;
const UNDO_STORAGE_KEY = 'fabric-admin-undo-import';

function readUndoImport() {
  try {
    const raw = localStorage.getItem(UNDO_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeUndoImport(payload) {
  if (!payload?.ids?.length) {
    localStorage.removeItem(UNDO_STORAGE_KEY);
    return;
  }
  localStorage.setItem(UNDO_STORAGE_KEY, JSON.stringify(payload));
}

function extractImportIds(data) {
  if (Array.isArray(data?.imported_ids) && data.imported_ids.length) {
    return data.imported_ids;
  }
  if (data?.fabric?.id) return [data.fabric.id];
  return (data?.results || [])
    .filter((result) => result.success && result.fabric?.id)
    .map((result) => result.fabric.id);
}

function authHeaders(extra = {}) {
  return {
    'Content-Type': 'application/json',
    ...extra
  };
}

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function formatCollectionImportMessage(summary, { prefix = 'Import complete' } = {}) {
  const {
    total_imported,
    total_discovered,
    total_failed,
    collection_title,
    api_product_count,
    pages_scraped,
    filter_note
  } = summary || {};
  let message =
    `${prefix}: ${total_imported} of ${total_discovered} fabrics imported from collection "${collection_title || 'collection'}"`;
  if (pages_scraped > 1) {
    message += ` across ${pages_scraped} pages`;
  }
  if (api_product_count && api_product_count > total_discovered) {
    message += ` (API returned ${api_product_count}; extras ignored)`;
  } else if (filter_note) {
    message += filter_note;
  }
  if (total_failed) message += ` (${total_failed} failed)`;
  return `${message}.`;
}

function formatPreviewMessage(preview) {
  if (!preview) return 'Checking URL…';
  if (preview.mode === 'collection') {
    let message = `Collection "${preview.collection_title || preview.collection_handle || 'collection'}" — ${preview.import_count} visible product(s) will be imported`;
    if (preview.pages_scraped > 1) {
      message += ` across ${preview.pages_scraped} pages`;
    }
    if (preview.api_product_count > preview.import_count) {
      message += ` (API returned ${preview.api_product_count}; extras ignored)`;
    }
    return message;
  }
  if (preview.mode === 'store') {
    return preview.warning || 'This URL will import the entire store catalog.';
  }
  return '1 product will be imported.';
}

function formatBulkSourceMessage(source) {
  if (!source?.success) {
    return `${source.url}: failed — ${source.error || 'Unknown error'}`;
  }
  if (source.mode === 'collection') {
    return `${source.url}: ${formatCollectionImportMessage(source.summary, { prefix: 'Imported' })}`;
  }
  if (source.mode === 'store') {
    const count = source.imported_count ?? source.summary?.total_imported ?? 0;
    return `${source.url}: imported ${count} fabrics from entire store.`;
  }
  return `${source.url}: 1 fabric imported.`;
}

export default function FabricAdminSection() {
  const [fabrics, setFabrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [bulkInput, setBulkInput] = useState('');
  const [adding, setAdding] = useState(false);
  const [bulkAdding, setBulkAdding] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [importNotice, setImportNotice] = useState(null);
  const [refreshingId, setRefreshingId] = useState(null);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [rescrapeStatus, setRescrapeStatus] = useState(null);
  const [editingTagsId, setEditingTagsId] = useState(null);
  const [tagDraft, setTagDraft] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [undoImport, setUndoImport] = useState(() => readUndoImport());
  const [undoing, setUndoing] = useState(false);

  function rememberImport(data, label) {
    const ids = extractImportIds(data);
    const payload = {
      ids,
      label: label || 'Last import',
      created_at: Date.now()
    };
    setUndoImport(payload.ids.length ? payload : null);
    writeUndoImport(payload.ids.length ? payload : null);
    return ids.length;
  }

  function showImportComplete(message, data) {
    const importedCount = rememberImport(data, message);
    setSuccessMessage(message);
    setImportNotice({
      type: 'success',
      message,
      importedCount
    });
  }

  async function loadRescrapeStatus() {
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/fabrics/rescrape-status`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) setRescrapeStatus(data.status || null);
    } catch {
      // ignore status load errors
    }
  }

  async function loadFabrics() {
    setLoading(true);
    setError('');
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/fabrics`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to load fabrics');
      setFabrics(data.fabrics || []);
    } catch (err) {
      setError(err.message || 'Failed to load fabrics');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadFabrics();
    loadRescrapeStatus();
  }, []);

  async function previewFabric(url) {
    const res = await adminFetch(`${API_BASE}/api/admin/fabrics/preview`, {
      method: 'POST',
      credentials: 'include',
      headers: authHeaders(),
      body: JSON.stringify({ url })
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Failed to preview fabric import');
    return data.preview;
  }

  async function addFabric(url) {
    const res = await adminFetch(`${API_BASE}/api/admin/fabrics`, {
      method: 'POST',
      credentials: 'include',
      headers: authHeaders(),
      body: JSON.stringify({ url })
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Failed to add fabric');
    return data;
  }

  async function handleAddSingle(e) {
    e.preventDefault();
    if (!urlInput.trim()) return;
    setAdding(true);
    setError('');
    setSuccessMessage('');
    setImportNotice({ type: 'importing', message: 'Checking URL…' });
    try {
      const trimmedUrl = urlInput.trim();
      const preview = await previewFabric(trimmedUrl);
      setImportNotice({
        type: 'importing',
        message: `${formatPreviewMessage(preview)} — importing now…`
      });
      const data = await addFabric(trimmedUrl);
      setUrlInput('');
      if (data.mode === 'collection') {
        showImportComplete(formatCollectionImportMessage(data.summary), data);
      } else if (data.mode === 'store') {
        const { total_imported, total_discovered, total_failed, source_url } = data.summary || {};
        const message =
          `Import complete: ${total_imported} of ${total_discovered} fabrics imported from ${source_url || 'store'}` +
          (total_failed ? ` (${total_failed} failed)` : '') + '.';
        showImportComplete(message, data);
      } else {
        showImportComplete('Import complete: 1 fabric link added.', data);
      }
      await loadFabrics();
    } catch (err) {
      setImportNotice({ type: 'error', message: err.message || 'Failed to add fabric' });
      setError(err.message || 'Failed to add fabric');
    } finally {
      setAdding(false);
    }
  }

  async function handleBulkAdd() {
    const urls = bulkInput
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    if (!urls.length) return;
    setBulkAdding(true);
    setError('');
    setSuccessMessage('');
    setImportNotice({ type: 'importing', message: `Checking ${urls.length} URL(s)…` });
    try {
      const previewLines = [];
      for (const url of urls) {
        const preview = await previewFabric(url);
        previewLines.push(`${url} → ${formatPreviewMessage(preview)}`);
        setImportNotice({
          type: 'importing',
          message: `${previewLines.join('\n')}\n\nImporting now…`
        });
      }

      const res = await adminFetch(`${API_BASE}/api/admin/fabrics/bulk`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
        body: JSON.stringify({ urls })
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Bulk add failed');
      const failed = (data.sources || data.results || []).filter((entry) => entry.success === false);
      const importedIds = extractImportIds(data);
      if (failed.length) {
        setError(`${failed.length} URL(s) failed: ${failed.map((f) => f.error || f.url).join('; ')}`);
      }
      if (importedIds.length) {
        const lines = (data.sources || []).map((source) => formatBulkSourceMessage(source));
        const message = lines.length
          ? `Import complete:\n${lines.join('\n')}`
          : `Import complete: ${importedIds.length} fabric link(s) added.`;
        showImportComplete(message, data);
      } else if (!failed.length) {
        setImportNotice({ type: 'error', message: 'No fabrics were imported.' });
      } else {
        setImportNotice({ type: 'error', message: 'Import finished with errors and no fabrics were added.' });
      }
      setBulkInput('');
      await loadFabrics();
    } catch (err) {
      setImportNotice({ type: 'error', message: err.message || 'Bulk add failed' });
      setError(err.message || 'Bulk add failed');
    } finally {
      setBulkAdding(false);
    }
  }

  async function refreshAllFabrics() {
    if (!fabrics.length) return;
    if (!window.confirm(`Re-scrape all ${fabrics.length} fabric link(s)? Prices, stock, and tags will be updated from each supplier site. This may take several minutes.`)) {
      return;
    }
    setRefreshingAll(true);
    setError('');
    setSuccessMessage('');
    setImportNotice({
      type: 'importing',
      message: `Re-scraping ${fabrics.length} fabric link(s)… this may take several minutes.`
    });
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/fabrics/refresh-all`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Re-scrape failed');
      const { total = 0, succeeded = 0, failed = 0, skipped = 0 } = data.summary || {};
      const message =
        `Re-scrape complete: ${succeeded} of ${total} fabric link(s) updated` +
        (skipped ? ` (${skipped} off limits)` : '') +
        (failed ? ` (${failed} failed)` : '') +
        '.';
      setImportNotice({ type: failed ? 'error' : 'success', message });
      setSuccessMessage(message);
      if (failed) {
        const failedResults = (data.results || []).filter((result) => !result.success);
        setError(`${failed} fabric(s) failed: ${failedResults.map((r) => r.error).filter(Boolean).slice(0, 3).join('; ')}`);
      }
      await loadFabrics();
      await loadRescrapeStatus();
    } catch (err) {
      setImportNotice({ type: 'error', message: err.message || 'Re-scrape failed' });
      setError(err.message || 'Re-scrape failed');
    } finally {
      setRefreshingAll(false);
    }
  }

  async function toggleOffLimits(fabric) {
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/fabrics/${fabric.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: authHeaders(),
        body: JSON.stringify({ rescrape_locked: !fabric.rescrape_locked })
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to update fabric');
      setFabrics((prev) => prev.map((item) => (item.id === fabric.id ? data.fabric : item)));
    } catch (err) {
      setError(err.message || 'Failed to update off-limits status');
    }
  }

  async function refreshFabric(id) {
    setRefreshingId(id);
    setError('');
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/fabrics/${id}/refresh`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Refresh failed');
      await loadFabrics();
    } catch (err) {
      setError(err.message || 'Refresh failed');
    } finally {
      setRefreshingId(null);
    }
  }

  async function deleteFabric(id) {
    if (!window.confirm('Delete this fabric link?')) return;
    const res = await adminFetch(`${API_BASE}/api/admin/fabrics/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    const data = await res.json();
    if (!data.success) {
      setError(data.error || 'Delete failed');
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    await loadFabrics();
  }

  function toggleSelected(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selectedIds.size === fabrics.length) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(fabrics.map((fabric) => fabric.id)));
  }

  async function bulkDeleteSelected() {
    if (!selectedIds.size) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected fabric link(s)?`)) return;
    setBulkDeleting(true);
    setError('');
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/fabrics/bulk-delete`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
        body: JSON.stringify({ ids: Array.from(selectedIds) })
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Bulk delete failed');
      setSuccessMessage(`Deleted ${data.deleted_count || selectedIds.size} fabric link(s).`);
      setSelectedIds(new Set());
      await loadFabrics();
    } catch (err) {
      setError(err.message || 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  }

  async function undoLastImport() {
    if (!undoImport?.ids?.length) return;
    if (!window.confirm(`Undo last import and delete ${undoImport.ids.length} fabric link(s)?`)) return;
    setUndoing(true);
    setError('');
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/fabrics/bulk-delete`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
        body: JSON.stringify({ ids: undoImport.ids })
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Undo failed');
      setSuccessMessage(`Undid import: removed ${data.deleted_count || undoImport.ids.length} fabric link(s).`);
      setImportNotice({
        type: 'success',
        message: `Undid import: removed ${data.deleted_count || undoImport.ids.length} fabric link(s).`
      });
      setUndoImport(null);
      writeUndoImport(null);
      await loadFabrics();
    } catch (err) {
      setError(err.message || 'Undo failed');
    } finally {
      setUndoing(false);
    }
  }

  async function saveTags(fabric) {
    const manualTags = tagDraft
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    const res = await adminFetch(`${API_BASE}/api/admin/fabrics/${fabric.id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: authHeaders(),
      body: JSON.stringify({ manual_tags: manualTags })
    });
    const data = await res.json();
    if (!data.success) {
      setError(data.error || 'Failed to save tags');
      return;
    }
    setEditingTagsId(null);
    setTagDraft('');
    await loadFabrics();
  }

  return (
    <>
      <p className="admin-panel-desc" style={{ marginTop: 0 }}>
        Paste a store URL, collection URL, or single product URL. Store and collection links import every fabric on that Shopify site automatically.
        The public page is unlisted at <code>/fabrics</code>.
      </p>

      {error && <p className="admin-login-error" style={{ marginBottom: 16 }}>{error}</p>}

      {importNotice && (
        <div
          className={`fabric-admin-import-notice fabric-admin-import-notice--${importNotice.type}`}
          role="status"
        >
          <p>{importNotice.message}</p>
          <div className="fabric-admin-import-notice-actions">
            {importNotice.type === 'success' && undoImport?.ids?.length > 0 && (
              <button
                type="button"
                className="admin-btn admin-btn--danger"
                onClick={undoLastImport}
                disabled={undoing}
              >
                {undoing ? 'Undoing…' : `Undo Import (${undoImport.ids.length})`}
              </button>
            )}
            {importNotice.type !== 'importing' && (
              <button
                type="button"
                className="admin-btn admin-btn--ghost"
                onClick={() => setImportNotice(null)}
              >
                Dismiss
              </button>
            )}
          </div>
        </div>
      )}

      {successMessage && !importNotice && (
        <p style={{ marginBottom: 16, fontFamily: 'IBM Plex Mono, monospace', fontSize: 12, color: '#1a7f37' }}>
          {successMessage}
        </p>
      )}

      <div className="admin-panel">
        <h3 className="admin-panel-title">Import Fabrics</h3>
        {undoImport?.ids?.length > 0 && (
          <div className="fabric-admin-undo-bar">
            <span>Last import ready to undo ({undoImport.ids.length} fabric{undoImport.ids.length === 1 ? '' : 's'})</span>
            <button
              type="button"
              className="admin-btn admin-btn--danger"
              onClick={undoLastImport}
              disabled={undoing || adding || bulkAdding || refreshingAll}
            >
              {undoing ? 'Undoing…' : 'Undo Last Import'}
            </button>
          </div>
        )}
        <form onSubmit={handleAddSingle} className="admin-toolbar" style={{ borderBottom: 'none', gap: 12 }}>
          <input
            type="url"
            placeholder="https://kbmfabrics.com or https://kofuyadenimworks.com/collections/..."
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            style={{ flex: 1, minWidth: 280, padding: '10px 12px' }}
          />
          <button type="submit" className="admin-btn" disabled={adding}>
            {adding ? 'Importing…' : 'Import'}
          </button>
          <a href="/fabrics" target="_blank" rel="noopener noreferrer" className="admin-btn admin-btn--ghost" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
            Preview Page →
          </a>
        </form>
        <p className="admin-panel-desc" style={{ marginTop: 12, marginBottom: 0 }}>
          Paste a <code>/collections/...</code> link for one collection, a store homepage for the full catalog, or a <code>/products/...</code> link for a single fabric.
        </p>
      </div>

      <div className="admin-panel">
        <h3 className="admin-panel-title">Bulk Add URLs</h3>
        <textarea
          rows={5}
          placeholder="One collection URL per line: https://kbmfabrics.com/collections/denim"
          value={bulkInput}
          onChange={(e) => setBulkInput(e.target.value)}
          style={{ width: '100%', padding: '10px 12px', marginBottom: 12 }}
        />
        <button type="button" className="admin-btn" onClick={handleBulkAdd} disabled={bulkAdding}>
          {bulkAdding ? 'Scraping…' : 'Bulk Add & Scrape'}
        </button>
        <p className="admin-panel-desc" style={{ marginTop: 12, marginBottom: 0 }}>
          One URL per line. Use the collection page URL (e.g. <code>/collections/denim</code>), not individual product links. Store homepages import the entire catalog.
        </p>
      </div>

      <div className="admin-panel" style={{ borderBottom: 'none' }}>
        <div className="admin-toolbar" style={{ borderBottom: '1px solid #000', marginBottom: 16, gap: 8 }}>
          <h3 className="admin-panel-title" style={{ margin: 0, flex: 1 }}>Fabric Links ({fabrics.length})</h3>
          <button
            type="button"
            className="admin-btn"
            onClick={refreshAllFabrics}
            disabled={refreshingAll || loading || !fabrics.length}
          >
            {refreshingAll ? 'Re-scraping…' : 'Re-scrape All'}
          </button>
          <button
            type="button"
            className="admin-btn admin-btn--danger"
            onClick={bulkDeleteSelected}
            disabled={bulkDeleting || selectedIds.size === 0}
          >
            {bulkDeleting ? 'Deleting…' : `Delete Selected (${selectedIds.size})`}
          </button>
        </div>
        <p className="admin-panel-desc" style={{ marginTop: 0, marginBottom: 16 }}>
          Re-scrape All updates every listed fabric from its supplier product page (price, availability, tags). Off-limits fabrics are hidden from the public page and skipped during re-scrape.
          {rescrapeStatus?.enabled
            ? ` Automatic re-scrape runs every ${rescrapeStatus.interval_hours || 24} hours` +
              (rescrapeStatus.last_run_at ? ` (last run: ${formatDate(rescrapeStatus.last_run_at)})` : '') +
              '.'
            : ' Automatic daily re-scrape is disabled on the server.'}
        </p>
        {loading ? (
          <p>Loading…</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={fabrics.length > 0 && selectedIds.size === fabrics.length}
                    onChange={toggleSelectAll}
                    aria-label="Select all fabrics"
                  />
                </th>
                <th>Image</th>
                <th>Name</th>
                <th>Brand</th>
                <th>Price</th>
                <th>Weight</th>
                <th>Availability</th>
                <th>Tags</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {fabrics.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center', padding: 24 }}>No fabric links yet.</td>
                </tr>
              ) : (
                fabrics.map((fabric) => (
                  <tr key={fabric.id} style={{ opacity: fabric.rescrape_locked ? 0.5 : 1 }}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(fabric.id)}
                        onChange={() => toggleSelected(fabric.id)}
                        aria-label={`Select ${fabric.name || 'fabric'}`}
                      />
                    </td>
                    <td>
                      {fabric.image_url ? (
                        <img src={fabric.image_url} alt="" style={{ width: 56, height: 56, objectFit: 'cover', border: '1px solid #000' }} />
                      ) : '—'}
                    </td>
                    <td style={{ maxWidth: 220 }}>
                      <div style={{ fontWeight: 600 }}>{fabric.name || '—'}</div>
                      <a href={fabric.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#666' }}>
                        View source
                      </a>
                    </td>
                    <td>{fabric.brand || '—'}</td>
                    <td>{fabric.price_text || (fabric.price != null ? `$${fabric.price}` : '—')}</td>
                    <td>{fabric.weight || '—'}</td>
                    <td>
                      <span style={{ color: fabric.availability === 'sold_out' ? '#c0392b' : '#000' }}>
                        {fabric.availability_text || fabric.availability || '—'}
                      </span>
                    </td>
                    <td style={{ maxWidth: 200 }}>
                      {editingTagsId === fabric.id ? (
                        <div>
                          <input
                            value={tagDraft}
                            onChange={(e) => setTagDraft(e.target.value)}
                            placeholder="tag-one, tag-two"
                            style={{ width: '100%', marginBottom: 6 }}
                          />
                          <button type="button" className="admin-btn" style={{ marginRight: 6 }} onClick={() => saveTags(fabric)}>Save</button>
                          <button type="button" className="admin-btn admin-btn--ghost" onClick={() => { setEditingTagsId(null); setTagDraft(''); }}>Cancel</button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {(fabric.tags || []).slice(0, 6).map((tag) => (
                            <span key={tag} style={{ fontSize: 11, border: '1px solid #000', padding: '2px 6px' }}>{formatFabricTagLabel(tag)}</span>
                          ))}
                          {(fabric.tags || []).length > 6 && <span style={{ fontSize: 11 }}>+{(fabric.tags || []).length - 6}</span>}
                        </div>
                      )}
                    </td>
                    <td>
                      <div>{fabric.scrape_status}</div>
                      {fabric.rescrape_locked && (
                        <div style={{ fontSize: 11, color: '#8b4513', fontWeight: 600 }}>Off limits (hidden)</div>
                      )}
                      {fabric.import_source_collection_handle && (
                        <div style={{ fontSize: 11, color: '#666' }}>collection: {fabric.import_source_collection_handle}</div>
                      )}
                      {fabric.import_source_type === 'store' && (
                        <div style={{ fontSize: 11, color: '#666' }}>store import</div>
                      )}
                      {fabric.scrape_error && <div style={{ fontSize: 11, color: '#c0392b' }}>{fabric.scrape_error}</div>}
                      <div style={{ fontSize: 11, color: '#666' }}>{formatDate(fabric.last_scraped_at)}</div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <button
                          type="button"
                          className="admin-btn admin-btn--ghost"
                          onClick={() => refreshFabric(fabric.id)}
                          disabled={refreshingId === fabric.id || fabric.rescrape_locked}
                          title={fabric.rescrape_locked ? 'Off limits — refresh disabled' : undefined}
                        >
                          {refreshingId === fabric.id ? 'Refreshing…' : 'Refresh'}
                        </button>
                        <button
                          type="button"
                          className="admin-btn admin-btn--ghost"
                          onClick={() => toggleOffLimits(fabric)}
                          title={fabric.rescrape_locked ? 'Show on public page and allow re-scrape' : 'Hide from public page and block re-scrape'}
                        >
                          {fabric.rescrape_locked ? 'Show on page' : 'Off limits'}
                        </button>
                        <button
                          type="button"
                          className="admin-btn admin-btn--ghost"
                          onClick={() => {
                            setEditingTagsId(fabric.id);
                            setTagDraft((fabric.manual_tags || []).join(', '));
                          }}
                        >
                          Edit Tags
                        </button>
                        <button type="button" className="admin-btn admin-btn--danger" onClick={() => deleteFabric(fabric.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
